
// Vue
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";

// Types
import {
	UserDetails,
	RecoveryQuestion,
	ResetPasswordResponse,
	ResetPasswordDetails,
	RecoveryStatus
} from "@/store/user-management/types";
import { Pref, PrefTypesEnum } from "@/store/settings/types";

// Third Party
import { validationMixin } from "vuelidate";
import { required, sameAs, requiredIf } from "vuelidate/lib/validators";
import { isEqual } from "lodash";

// Components
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vSelect from "vue-select";

// Namespaces
const UserManagement = namespace("userManagement");
const SettingsStore = namespace("settings");

@Component({
	mixins: [validationMixin],
	validations: {
		newPassword: {
			required,
			sameAs: sameAs(function() {
				return this.confirmPassword;
			})
		},
		confirmPassword: {
			required
		},
		userAnswer1: {
			required: requiredIf(function() {
				return !this.userAnswer2 || !this.userAnswer3;
			})
		},
		userAnswer2: {
			required: requiredIf(function() {
				return !this.userAnswer1 || !this.userAnswer3;
			})
		},
		userAnswer3: {
			required: requiredIf(function() {
				return !this.userAnswer1 || !this.userAnswer2;
			})
		}
	},
	components: {
		"v-select": vSelect,
		"vue-perfect-scrollbar": VuePerfectScrollbar
	}
})
export default class ResetPasswordModal extends Vue {
	@Prop({ default: false, type: Boolean }) visible: boolean;
	@Prop({ default: null, type: String }) providedUsername: string;

	@UserManagement.Action private getUserQuestions: (username: string) => Promise<RecoveryQuestion[]>;
	@UserManagement.Action private resetUserPassword: (
		resetDetails: ResetPasswordDetails
	) => Promise<ResetPasswordResponse>;

	private complexityChecks: { [key: string]: number } = null;

	// User questions
	private userQuestions: RecoveryQuestion[] = [];
	private showQuestions: boolean = false;

	// Flags and Messages
	private showWarning: boolean = false;
	private warningMessage: string = null;
	private showSuccess: boolean = false;

	// Form details
	private username: string = null;
	private userAnswer1: string = null;
	private userAnswer2: string = null;
	private userAnswer3: string = null;
	private newPassword: string = null;
	private confirmPassword: string = null;

	private async mounted() {
		this.username = this.providedUsername;
	}

	@Emit("close-modal")
	private closeModal() {
		this.username = null;
		this.userQuestions = [];

		this.showQuestions = false;
		this.showWarning = false;
		this.showSuccess = false;

		this.warningMessage = null;

		this.userAnswer1 = null;
		this.userAnswer2 = null;
		this.userAnswer3 = null;
		this.newPassword = null;
		this.confirmPassword = null;
	}

	private async resetPassword() {
		this.showWarning = false;
		this.showSuccess = false;
		let resetResponse = await this.resetUserPassword({
			userName: this.username,
			answer1: this.userAnswer1,
			answer2: this.userAnswer2,
			answer3: this.userAnswer3,
			newPassword: this.newPassword
		});

		if (resetResponse.status == RecoveryStatus.Success) {
			this.showSuccess = true;
		} else {
			this.warningMessage = resetResponse.validationMessage;
			this.showWarning = true;
			this.complexityChecks = resetResponse.complexityChecks;
		}
	}

	private async showSecretQuestions() {
		this.userQuestions = await this.getUserQuestions(this.username);
		// get modal here
		if (this.userQuestions.length == 3) {
			this.showQuestions = true;
		} else {
			this.showQuestions = false;
		}
	}
}
